import { authService } from './authService';
import { apiFetch } from './apiFetch';
import { ACTIVE_URL } from '../config';
import { parseErrorResponse } from '../utils/errorHandling';

const API_BASE_URL = `${ACTIVE_URL}/api/v1/dms`;

const getHeaders = (isFormData = false) => {
  const token = authService.getAccessToken();
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`
  };
  
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    throw await parseErrorResponse(response);
  }
  
  // For 204 No Content
  if (response.status === 204) {
    return null;
  }
  
  return response.json();
};

export const dmsService = {
  // --- Categories ---
  async getCategories() {
    const response = await apiFetch(`${API_BASE_URL}/categories/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createCategory(data: { name: string; description?: string; department?: string | number }) {
    const response = await apiFetch(`${API_BASE_URL}/categories/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // --- Document Types ---
  async getDocumentTypes() {
    const response = await apiFetch(`${API_BASE_URL}/document-types/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createDocumentType(data: { name: string; description?: string }) {
    const response = await apiFetch(`${API_BASE_URL}/document-types/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // --- Documents ---
  async getDocuments(params?: Record<string, string>) {
    let url = `${API_BASE_URL}/documents/`;
    if (params) {
      const queryString = new URLSearchParams(params).toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    const response = await apiFetch(url, { headers: getHeaders() });
    return handleResponse(response);
  },

  async getArchivedDocuments(params?: Record<string, string>) {
    let url = `${API_BASE_URL}/documents/archived/`;
    if (params) {
      const queryString = new URLSearchParams(params).toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    const response = await apiFetch(url, { headers: getHeaders() });
    return handleResponse(response);
  },

  async getDocument(id: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${id}/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async uploadDocument(formData: FormData) {
    const response = await apiFetch(`${API_BASE_URL}/documents/`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
    return handleResponse(response);
  },

  async updateDocument(id: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${id}/`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async replaceDocumentVersion(id: string | number, formData: FormData) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${id}/replace/`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
    return handleResponse(response);
  },

  async archiveDocument(id: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${id}/archive/`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async deleteDocument(id: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${id}/`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async downloadDocument(id: string | number, version?: number) {
    let url = `${API_BASE_URL}/documents/${id}/download/`;
    if (version) {
      url += `?version=${version}`;
    }
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },


  async getDocumentHistory(id: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${id}/history/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async archiveDocumentVersion(documentId: string | number, versionId: string) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${documentId}/versions/${versionId}/archive/`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async restoreDocumentVersion(documentId: string | number, versionId: string) {
    const response = await apiFetch(`${API_BASE_URL}/documents/${documentId}/versions/${versionId}/restore/`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },


  async shareDocument(id: string | number, recipientEmail: string, version?: number) {
    const body: Record<string, any> = { recipient_email: recipientEmail };
    if (version !== undefined) body.version = version;
    const response = await apiFetch(`${API_BASE_URL}/documents/${id}/share/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  },

  // --- Audit Logs ---
  async getAuditLogs() {
    const response = await apiFetch(`${API_BASE_URL}/audit-logs/`, { headers: getHeaders() });
    return handleResponse(response);
  }
};
